import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowRight, ChevronLeft, ChevronRight, Home } from 'lucide-react';
import { getListings, getListingStats, trackEvent } from '../lib/api';
import { ListingCard, ListingCardSkeleton } from '../components/ListingCard';
import { PoweredByRealtor, CreaDisclaimer } from '../components/CreaCompliance';
import SEO from '../components/SEO';

export default function HomesForSale() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);

  const neighbourhood = searchParams.get('neighbourhood') || '';
  const type = searchParams.get('type') || '';
  const sort = searchParams.get('sort') || 'newest';
  const page = parseInt(searchParams.get('page') || '1');

  useEffect(() => {
    trackEvent({ event_type: 'page_view', page: 'homes-for-sale' });
    getListingStats().then(r => setStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { page, limit: 24, sort };
    if (neighbourhood) params.neighbourhood = neighbourhood;
    if (type) params.type = type;
    getListings(params)
      .then(r => { setListings(r.data.listings || []); setTotal(r.data.total || 0); setPages(r.data.pages || 1); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [neighbourhood, type, sort, page]);

  const updateFilter = (key, value) => {
    const p = new URLSearchParams(searchParams);
    if (value) p.set(key, value); else p.delete(key);
    p.set('page', '1');
    setSearchParams(p);
  };

  return (
    <div className="min-h-screen pt-16" data-testid="homes-for-sale-page">
      <SEO
        title={`${total} Homes for Sale in Burlington, ON${neighbourhood ? ` — ${neighbourhood.replace(/-/g, ' ')}` : ''}`}
        description={`Browse ${total} active MLS® listings in Burlington, Ontario. Detached homes, condos, townhomes — updated daily. Powered by REALTOR.ca.`}
        canonical="/homes-for-sale"
        breadcrumbs={[{ name: 'Home', url: '/' }, { name: 'Homes for Sale', url: '/homes-for-sale' }]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "Homes for Sale in Burlington, Ontario",
          "numberOfItems": total,
          "itemListElement": listings.slice(0, 10).map((l, i) => ({
            "@type": "ListItem",
            "position": i + 1,
            "url": `https://buyinburlington.ca/listing/${l.mls_number}`,
            "name": `${l.address?.street || ''}, Burlington — $${l.price?.toLocaleString() || 'TBD'}`
          }))
        }}
      />

      {/* Hero */}
      <section className="bg-white border-b border-slate-200 py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-2 mb-2">
            <Home className="w-4 h-4 text-emerald-600" />
            <span className="text-xs font-semibold tracking-widest uppercase text-emerald-600">Burlington MLS® Listings</span>
          </div>
          <h1 className="text-3xl md:text-4xl tracking-tight mb-2" style={{ fontFamily: "'Instrument Serif', serif" }}>
            Homes for Sale in <span className="italic">Burlington</span>
          </h1>
          <p className="text-base text-slate-500">{total} active listings &middot; Updated daily</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-8">
          {/* Neighbourhood chips */}
          <div className="flex flex-wrap gap-2">
            <button onClick={() => updateFilter('neighbourhood', '')} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${!neighbourhood ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-200 text-slate-600 hover:border-slate-400'}`}>
              All Areas
            </button>
            {stats?.by_neighbourhood?.filter(s => s.count > 0).map(s => (
              <button key={s.neighbourhood} onClick={() => updateFilter('neighbourhood', s.neighbourhood)} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${neighbourhood === s.neighbourhood ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-200 text-slate-600 hover:border-slate-400'}`}>
                {s.neighbourhood?.replace(/-/g, ' ')} <span className="text-slate-400 ml-1">{s.count}</span>
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 ml-auto">
            <Select value={type || 'all'} onValueChange={v => updateFilter('type', v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[130px] h-9 text-xs rounded-lg"><SelectValue placeholder="All Types" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="detached">Detached</SelectItem>
                <SelectItem value="townhome">Townhome</SelectItem>
                <SelectItem value="condo">Condo</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sort} onValueChange={v => updateFilter('sort', v)}>
              <SelectTrigger className="w-[130px] h-9 text-xs rounded-lg"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest</SelectItem>
                <SelectItem value="price_asc">Price: Low</SelectItem>
                <SelectItem value="price_desc">Price: High</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => <ListingCardSkeleton key={i} />)}
          </div>
        ) : listings.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {listings.map(l => <ListingCard key={l.mls_number || l.ddf_id} listing={l} />)}
          </div>
        ) : (
          <div className="text-center py-20">
            <p className="text-slate-400">No listings found matching your criteria.</p>
            <Button onClick={() => setSearchParams({})} variant="outline" className="rounded-full mt-4 text-xs">Clear Filters</Button>
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-center gap-3 mt-10">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => updateFilter('page', String(page - 1))} className="rounded-lg h-9">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-xs font-mono text-slate-500">{page} / {pages}</span>
            <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => updateFilter('page', String(page + 1))} className="rounded-lg h-9">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}

        {/* Blueprint CTA */}
        <div className="mt-12">
          <div className="bg-slate-50 rounded-2xl p-8 text-center max-w-lg mx-auto">
            <h3 className="text-xl tracking-tight mb-2" style={{ fontFamily: "'Instrument Serif', serif" }}>Not sure which area to search?</h3>
            <p className="text-sm text-slate-500 mb-4">The Buyer Blueprint matches you to the 2-3 Burlington neighbourhoods that fit your budget and priorities.</p>
            <Link to="/blueprint">
              <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-6 h-10 text-sm font-medium">
                Start Blueprint <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* CREA Compliance — subtle footer strip, separate from main content */}
      <div className="border-t border-slate-100 mt-12 py-6 bg-slate-50/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
          <PoweredByRealtor className="h-8 opacity-60 flex-shrink-0" />
          <CreaDisclaimer className="text-center sm:text-left" />
        </div>
      </div>
    </div>
  );
}
